import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { toast } from "sonner";
import { Search, Plus } from "lucide-react";
import { Tilt3D } from "@/components/Tilt3D";

const SIZES = [
  { key: "Small", mult: 1.0 },
  { key: "Medium", mult: 1.5 },
  { key: "Large", mult: 2.0 },
];

const FILTERS = [
  { key: "all", label: "All" },
  { key: "veg", label: "Veg" },
  { key: "sugar-free", label: "Sugar Free" },
  { key: "premium", label: "Premium" },
  { key: "natural", label: "Natural" },
  { key: "chocolate", label: "Chocolate" },
  { key: "fruit", label: "Fruit" },
];

export default function Menu() {
  const [flavours, setFlavours] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("cat") || "");
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const { add } = useCart();

  useEffect(() => {
    api
      .get("/flavours")
      .then((r) => {
        const data = Array.isArray(r.data)
          ? r.data
          : Array.isArray(r.data?.flavours)
          ? r.data.flavours
          : [];

        setFlavours(data);
      })
      .catch((error) => {
        console.error("Failed to load flavours:", error);
        setFlavours([]);
      });

    api
      .get("/menu-items")
      .then((r) => {
        const data = Array.isArray(r.data)
          ? r.data
          : Array.isArray(r.data?.menu_items)
          ? r.data.menu_items
          : Array.isArray(r.data?.items)
          ? r.data.items
          : [];

        setMenuItems(data);
      })
      .catch((error) => {
        console.error("Failed to load menu items:", error);
        setMenuItems([]);
      });
  }, []);

  const categories = useMemo(() => {
  const cats = [];

  if (flavours.length > 0) {
    cats.push("Ice Cream Scoops");
  }

  const catset = new Set(menuItems.map((m) => m.category));
  catset.forEach((c) => {
    if (c && !cats.includes(c)) {
      cats.push(c);
    }
  });

  return cats;
}, [flavours, menuItems]);

useEffect(() => {
  if (!tab && categories.length > 0) {
    setTab(categories[0]);
  }
}, [tab, categories]);

  const displayed = useMemo(() => {
    const query = search.trim().toLowerCase();

    // =====================================================
    // NO SEARCH → NORMAL CATEGORY VIEW
    // =====================================================

    if (!query) {
      if (tab === "Ice Cream Scoops") {
        let arr = flavours;

        if (filter !== "all") {
          arr = arr.filter(
            (f) =>
              Array.isArray(f.tags) &&
              f.tags.some(
                (tag) =>
                  String(tag).toLowerCase() ===
                  filter.toLowerCase()
              )
          );
        }

        return arr.map((f) => ({
          ...f,
          _type: "flavour",
        }));
      }

      return menuItems
        .filter((m) => m.category === tab)
        .map((m) => ({
          ...m,
          _type: "menu",
        }));
    }

    // =====================================================
    // SEARCH ACTIVE → SEARCH ENTIRE MENU
    // =====================================================

    const flavourResults = flavours.filter((f) => {
      const name = String(f.name || "").toLowerCase();

      const description = String(
        f.description || ""
      ).toLowerCase();

      const tags = Array.isArray(f.tags)
        ? f.tags
            .map((tag) => String(tag).toLowerCase())
            .join(" ")
        : String(f.tags || "").toLowerCase();

      return (
        name.includes(query) ||
        description.includes(query) ||
        tags.includes(query)
      );
    });

    const menuResults = menuItems.filter((m) => {
      const name = String(m.name || "").toLowerCase();

      const description = String(
        m.description || ""
      ).toLowerCase();

      const category = String(
        m.category || ""
      ).toLowerCase();

      const tags = Array.isArray(m.tags)
        ? m.tags
            .map((tag) => String(tag).toLowerCase())
            .join(" ")
        : String(m.tags || "").toLowerCase();

      return (
        name.includes(query) ||
        description.includes(query) ||
        category.includes(query) ||
        tags.includes(query)
      );
    });

    return [
      ...flavourResults.map((f) => ({
        ...f,
        _type: "flavour",
      })),

      ...menuResults.map((m) => ({
        ...m,
        _type: "menu",
      })),
    ];
  }, [
    tab,
    filter,
    search,
    flavours,
    menuItems,
  ]);

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-12 py-8 md:py-12 relative overflow-x-hidden">
      <div className="absolute top-40 -right-20 w-96 h-96 rounded-full bg-[#FFB7B2]/20 blur-3xl pointer-events-none" />
      <div className="absolute top-96 -left-20 w-80 h-80 rounded-full bg-[#A8E6CF]/20 blur-3xl pointer-events-none" />
      <div className="mb-8 relative">
        <div className="text-xs uppercase tracking-widest text-[#B8E14A] mb-2">Our Menu</div>
        <h1 className="font-heading text-3xl sm:text-4xl md:text-6xl">A World of Sweet Possibilities</h1>
      </div>

      {/* Search */}
      <div className="glass rounded-full p-2 pl-6 flex items-center gap-2 mb-8 max-w-xl">
        <Search className="w-4 h-4 text-[#B7D8D5]" />
        <input data-testid="menu-search" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search flavours, shakes, waffles..." className="flex-1 bg-transparent outline-none py-2 text-sm" />
      </div>

      {/* Category tabs */}
      <div className="-mx-4 md:mx-0 px-4 md:px-0 flex gap-2 overflow-x-auto pb-3 mb-6 scrollbar-thin">
        {categories.map((c) => (
          <button key={c} data-testid={`menu-tab-${c.toLowerCase().replace(/\s/g,'-')}`}
            onClick={() => setTab(c)}
            className={`whitespace-nowrap px-5 py-2.5 rounded-full text-sm transition ${tab === c ? "bg-[#B8E14A] text-[#1F5F5C]" : "bg-white text-[#1F5F5C] text-[#1F5F5C] border border-[rgba(184,225,74,0.35)] hover:border-[#B8E14A]"}`}>
            {c}
          </button>
        ))}
      </div>

      {/* Sub-filters only for scoops */}
      {tab === "Ice Cream Scoops" && (
        <div className="-mx-4 md:mx-0 px-4 md:px-0 flex gap-2 overflow-x-auto pb-3 mb-8">
          {FILTERS.map((f) => (
            <button key={f.key} data-testid={`filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`whitespace-nowrap px-4 py-1.5 rounded-full text-xs uppercase tracking-wide transition ${filter === f.key ? "bg-[#B8E14A] text-[#1F5F5C]" : "bg-white/80 text-[#1F5F5C] border border-[rgba(184,225,74,0.35)]"}`}>
              {f.label}
            </button>
          ))}
        </div>
      )}

      {/* Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {displayed.map((item, i) => (
          <ProductCard key={item.id} item={item} index={i} onAdd={add} isFlavour={item._type === "flavour"} />
        ))}
        {displayed.length === 0 && (
          <div className="col-span-full text-center py-16 text-[#B7D8D5]">No items found.</div>
        )}
      </div>
    </div>
  );
}

function ProductCard({ item, index, onAdd, isFlavour }) {
  const [size, setSize] = useState("Small");
  const [cone, setCone] = useState("Cup");
  const sizeMult = SIZES.find((s) => s.key === size)?.mult || 1;
  const conePrice = cone === "Cone" ? 15 : 0;
  const price = Math.round(item.price * sizeMult + conePrice);

  const handleAdd = () => {
    onAdd({
      kind: isFlavour ? "flavour" : "menu",
      itemId: item.id,
      name: item.name,
      image: item.image,
      price,
      options: isFlavour ? { size, cone } : {},
    });
    toast.success(`${item.name} added to cart`);
  };

  return (
    <motion.div initial={{ opacity: 0, y: 30, rotateX: -10 }} whileInView={{ opacity: 1, y: 0, rotateX: 0 }} viewport={{ once: true }}
      transition={{ delay: index * 0.04, duration: 0.6 }} style={{ perspective: 1000 }} data-testid={`product-card-${item.id}`}>
      <Tilt3D max={8} className="rounded-3xl">
        <div className="glass rounded-3xl overflow-hidden card-hover">
          <div className="aspect-[4/3] overflow-hidden">
            <img src={item.image} alt={item.name} className="w-full h-full object-cover transition-transform duration-700 hover:scale-110" />
          </div>
          <div className="p-5">
            <div className="flex items-start justify-between gap-2 mb-1">
              <h3 className="font-heading text-xl leading-tight">{item.name}</h3>
              <span className="font-semibold text-[#B8E14A] whitespace-nowrap">₹{price}</span>
            </div>
            <p className="text-sm text-[#E6F4F2] line-clamp-2 mb-3">{item.description}</p>
            {isFlavour && (
              <div className="text-xs text-[#B7D8D5] mb-3">{item.calories} cal · {item.rating}★</div>
            )}
            {isFlavour && (
              <>
                <div className="flex gap-1 mb-2">
                  {SIZES.map((s) => (
                    <button key={s.key} data-testid={`size-${item.id}-${s.key}`}
                      onClick={() => setSize(s.key)}
                      className={`flex-1 py-1.5 text-xs rounded-full border transition ${size === s.key ? "bg-[#B8E14A] text-[#1F5F5C] border-[#B8E14A]" : "border-[rgba(184,225,74,0.35)]"}`}>
                      {s.key}
                    </button>
                  ))}
                </div>
                <div className="flex gap-1 mb-4">
                  {["Cup", "Cone"].map((c) => (
                    <button key={c} data-testid={`cone-${item.id}-${c}`}
                      onClick={() => setCone(c)}
                      className={`flex-1 py-1.5 text-xs rounded-full border transition ${cone === c ? "bg-[#B8E14A] text-white border-[#B8E14A]" : "border-[rgba(184,225,74,0.35)]"}`}>
                      {c === "Cone" ? "Cone (+₹15)" : c}
                    </button>
                  ))}
                </div>
              </>
            )}
            <button data-testid={`add-to-cart-${item.id}`} onClick={handleAdd}
              className="w-full btn-primary justify-center !py-2.5">
              <Plus className="w-4 h-4" /> Add to Cart
            </button>
          </div>
        </div>
      </Tilt3D>
    </motion.div>
  );
}
